/*
 * Copyright (c) 2019 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { ApplicationError, CoreErrorKey, HttpHeaders, HttpMethod, HttpMethods } from '@typescript-kit/core';
import { HttpApplicationError } from '../model/http-error.model';

@Injectable()
export class HttpService {
  public defaultHeaders: { [key: string]: string } = {
    [HttpHeaders.CONTENT_TYPE]: 'application/json'
  };

  constructor(private httpClient: HttpClient) {
  }

  async get(
    url: string, query?: { [key: string]: any }, headers?: { [key: string]: string }
  ): Promise<any> {
    return this.request(HttpMethods.GET, url, undefined, query, headers);
  }

  async post(
    url: string, data?: any, query?: { [key: string]: any }, headers?: { [key: string]: string }
  ): Promise<any> {
    return this.request(HttpMethods.POST, url, data, query, headers);
  }

  async put(
    url: string, data?: any, query?: { [key: string]: any }, headers?: { [key: string]: string }
  ): Promise<any> {
    return this.request(HttpMethods.PUT, url, data, query, headers);
  }

  async delete(
    url: string, query?: { [key: string]: any }, headers?: { [key: string]: string }
  ): Promise<any> {
    return this.request(HttpMethods.DELETE, url, undefined, query, headers);
  }

  async request(
    method: HttpMethod,
    url: string,
    body: any,
    query?: { [key: string]: any },
    headers?: { [key: string]: string }
  ): Promise<any> {
    const urlWithQuery = this.appendQuery(url, query);
    headers = this.appendDefaultHeaders(headers);
    const capturedStack: string = new Error().stack;
    return new Promise<any>((resolve, reject) => {
      this.httpClient.request(method, urlWithQuery, { body, headers, observe: 'response', withCredentials: false, reportProgress: false })
        .subscribe({
          next: (response: HttpResponse<any>) => {
            resolve(response.body && response.body.data);
          },
          error: (response: HttpErrorResponse) => {
            const errorData = response.error;
            let error: ApplicationError;
            if (!errorData) {
              // error data missing
              error = new ApplicationError({
                key: CoreErrorKey.UNDEFINED_ERROR,
                arguments: { response, error: response.message || null }
              });
            } else if (errorData instanceof ErrorEvent || errorData instanceof ProgressEvent) {
              // a client-side or network error occurred.
              error = new ApplicationError({
                key: CoreErrorKey.NETWORK_ERROR,
                arguments: { response, error: 'message' in errorData && errorData.message || null }
              });
            } else if (!errorData.error) {
              // an unknown server-side error occurred (no error object).
              error = new ApplicationError({
                key: CoreErrorKey.UNDEFINED_ERROR,
                arguments: { response, error: errorData.message || errorData }
              });
            } else if (!('key' in errorData.error)) {
              // an unknown server-side error occurred (undefined error object).
              error = new ApplicationError({
                key: CoreErrorKey.UNDEFINED_ERROR,
                arguments: { response, error: errorData.error.message || errorData.error }
              });
            } else {
              error = new ApplicationError(errorData.error);
            }
            reject(new HttpApplicationError(method, decodeURIComponent(urlWithQuery), error, response.status, capturedStack));
          }
        });
    });
  }

  appendDefaultHeaders(headers: { [key: string]: string }) {
    return headers ? Object.assign({}, this.defaultHeaders, headers) : this.defaultHeaders;
  }

  appendQuery(url: string, query: { [key: string]: any }): string {
    if (!query) {
      return url;
    }
    const keys = Object.keys(query).filter(key => query[key] !== undefined);
    if (keys.length === 0) {
      return url;
    }
    const queryString = keys.map((key: string) => {
      const encodedKey = encodeURIComponent(key);
      const value = query[key];
      if (value === undefined || value === null || value === '') {
        return encodedKey;
      }
      const encodedValue = encodeURIComponent(typeof value === 'string' ? value : JSON.stringify(value));
      return `${encodedKey}=${encodedValue}`;
    }).join('&');
    if (!queryString) {
      return url;
    }
    const querySeparator = /[?]/.test(url) ? '&' : '?';
    return `${url}${querySeparator}${queryString}`;
  }
}
